export const MARKETPLACE_CHECKOUT_RETURN_TO_STORAGE_KEY = 'marketplace.checkout.returnTo'
export const DEFAULT_MARKETPLACE_CHECKOUT_RETURN_PATH = '/search'

const MARKETPLACE_CHECKOUT_RETURN_TO_COOKIE_KEY = 'marketplace_checkout_return_to'

const BLOCKED_PATH_PREFIXES = [
  '/auth',
  '/checkout',
  '/login',
  '/logout',
  '/marketplace/success',
  '/register',
  '/signup',
]

function getStoredCookieValue(name: string) {
  if (typeof document === 'undefined') {
    return null
  }

  const value = `; ${document.cookie}`
  const cookieValue = value.split(`; ${name}=`).pop()?.split(';').shift()

  if (!cookieValue) {
    return null
  }

  try {
    return decodeURIComponent(cookieValue)
  } catch {
    return null
  }
}

function storeCookieValue(name: string, value: string) {
  if (typeof document === 'undefined') {
    return
  }

  const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function getStoredWebStorageValue(storage: 'sessionStorage' | 'localStorage') {
  try {
    return window[storage].getItem(MARKETPLACE_CHECKOUT_RETURN_TO_STORAGE_KEY)
  } catch {
    return null
  }
}

function storeWebStorageValue(storage: 'sessionStorage' | 'localStorage', value: string) {
  try {
    window[storage].setItem(MARKETPLACE_CHECKOUT_RETURN_TO_STORAGE_KEY, value)
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function isBlockedReturnPath(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/'
  const pathSegments = normalizedPathname.split('/').filter(Boolean)

  if (pathSegments.includes('checkout') || pathSegments.includes('success')) {
    return true
  }

  if (normalizedPathname === '/marketplace' || normalizedPathname.startsWith('/marketplace/')) {
    return true
  }

  return BLOCKED_PATH_PREFIXES.some(
    (blockedPath) => normalizedPathname === blockedPath || normalizedPathname.startsWith(`${blockedPath}/`)
  )
}

export function getSafeMarketplaceCheckoutReturnPath(value: string | null | undefined) {
  if (!value || typeof window === 'undefined') {
    return null
  }

  let url: URL

  try {
    url = new URL(value, window.location.origin)
  } catch {
    return null
  }

  if (url.origin !== window.location.origin || isBlockedReturnPath(url.pathname)) {
    return null
  }

  return `${url.pathname}${url.search}${url.hash}`
}

export function getStoredMarketplaceCheckoutReturnPath() {
  const storedValues = [
    getStoredWebStorageValue('sessionStorage'),
    getStoredWebStorageValue('localStorage'),
    getStoredCookieValue(MARKETPLACE_CHECKOUT_RETURN_TO_COOKIE_KEY),
  ]

  for (const storedValue of storedValues) {
    const returnPath = getSafeMarketplaceCheckoutReturnPath(storedValue)

    if (returnPath) {
      return returnPath
    }
  }

  return null
}

export function storeMarketplaceCheckoutReturnUrl(value: string | null | undefined) {
  const returnPath = getSafeMarketplaceCheckoutReturnPath(value)

  if (!returnPath) {
    return null
  }

  storeWebStorageValue('sessionStorage', returnPath)
  storeWebStorageValue('localStorage', returnPath)
  storeCookieValue(MARKETPLACE_CHECKOUT_RETURN_TO_COOKIE_KEY, returnPath)

  return returnPath
}

export function storeCurrentMarketplaceCheckoutReturnUrl() {
  if (typeof window === 'undefined') {
    return null
  }

  return storeMarketplaceCheckoutReturnUrl(window.location.href)
}

export function storeMarketplaceCheckoutReferrerReturnUrl() {
  const storedReturnPath = getStoredMarketplaceCheckoutReturnPath()

  if (typeof document === 'undefined' || storedReturnPath) {
    return storedReturnPath
  }

  return storeMarketplaceCheckoutReturnUrl(document.referrer)
}
